/**
 * Referrals Page - Trade Loop v2.0
 *
 * Displays user's referrals with comprehensive filtering:
 * - Search by partner name, company name
 * - Filter by Sent/Received/All
 * - Filter by Status (Pending, Accepted, Declined, Completed, Expired, Canceled)
 * - Filter by Date range
 * - Filter by Partner
 *
 * Uses AppLayout with cookie-based authentication
 */

import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ApiResponse, Referral, extractArrayData } from "@/types/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
  Briefcase,
  Search,
  Filter,
  Calendar as CalendarIcon,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
} from "lucide-react";
import AppLayout from "@/components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeDashboard } from "@/hooks";
import CompactReferralCard from "@/components/CompactReferralCard";
import ReferralDetailModal from "@/components/ReferralDetailModal";
import { useToast } from "@/contexts/ToastContext";
import { api } from "@/lib/api";
import AvailabilityRequestsPanel from "@/components/AvailabilityRequestsPanel";

// Referral status options - these are the rows in referral_statuses that the app
// actually writes. "Hidden" is deliberately NOT here: hiding is per-user
// (referrals.hidden_for) and independent of status, so it gets its own filter.
// Anything listed here that the database cannot produce silently returns zero
// results, which is why "Expired" (no such status) and "Canceled" (the column
// stores the British "cancelled") are gone and "Closed" was added.
const REFERRAL_STATUSES = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "closed", label: "Closed" },
];

// Hidden is orthogonal to status, so it filters on its own axis. This is what
// makes "every pending referral, hidden or not" expressible.
const HIDDEN_FILTERS = [
  { value: "exclude", label: "Hide hidden" },
  { value: "include", label: "Include hidden" },
  { value: "only", label: "Only hidden" },
] as const;

type HiddenFilter = (typeof HIDDEN_FILTERS)[number]['value'];

// Urgency level options
const URGENCY_LEVELS = [
  { value: "all", label: "All Urgency Levels" },
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "urgent", label: "Urgent" },
];


export default function ReferralsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [showFilters, setShowFilters] = useState(false);
  // Show inactive referrals toggle - default to hiding inactive (closed, completed, canceled)
  // Only show all if explicitly set via URL param showAll=true
  const [showInactiveReferrals, setShowInactiveReferrals] = useState(() => {
    return searchParams.get('showAll') === 'true';
  });
  // Hidden referrals filter - independent of status, defaults to hiding them
  const [hiddenFilter, setHiddenFilter] = useState<HiddenFilter>('exclude');
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  // Inactive statuses that should be hidden by default (active = pending, accepted)
  // Note: DB stores 'cancelled' (British); 'canceled' (US) is kept defensively since the type allows both
  const INACTIVE_STATUSES = ['declined', 'expired', 'completed', 'closed', 'canceled', 'cancelled'];

  // Pagination state (20 items per page)
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 20;

  // Subscribe to realtime updates for referrals
  useRealtimeDashboard({ userId: user?.id, enabled: !!user?.id });

  // Automatically claim referral if claimToken is present in URL, or if a
  // pendingReferralInviteToken was stashed in localStorage during the
  // unauthenticated → authenticated transition (LoginPage/AuthCallback).
  useEffect(() => {
    const urlClaimToken = searchParams.get('claimToken');
    const stashedToken = typeof window !== 'undefined'
      ? localStorage.getItem('pendingReferralInviteToken')
      : null;
    const claimToken = urlClaimToken || stashedToken;

    if (claimToken && user?.id) {
      const claimReferral = async () => {
        try {
          console.log('[ReferralsPage] Attempting to claim referral with token:', claimToken);

          const response = await api.post('/api/workflows/referral/accept-invitation', {
            token: claimToken
          });

          if (response.success) {
            showSuccess('Referral Claimed!', 'You have successfully accepted this referral');
            console.log('[ReferralsPage] Referral claimed successfully:', response.data);

            // Clear stashed token now that it's been redeemed
            localStorage.removeItem('pendingReferralInviteToken');

            // Remove claimToken from URL
            if (urlClaimToken) {
              searchParams.delete('claimToken');
              setSearchParams(searchParams);
            }

            // Refresh the referrals list to show the newly claimed referral
            window.location.reload();
          } else {
            showError('Failed to Claim', 'Unable to claim this referral');
            console.error('[ReferralsPage] Failed to claim referral:', response);
          }
        } catch (error: any) {
          console.error('[ReferralsPage] Error claiming referral:', error);

          if (error.response?.status === 404) {
            showError('Invalid Referral', 'This referral has already been claimed or does not exist');
          } else if (error.response?.status === 400) {
            showError('Cannot Claim', 'You cannot accept your own referral invitation');
          } else {
            showError('Claim Failed', 'An error occurred while claiming the referral');
          }

          // Clear invalid token from both URL and localStorage so we don't
          // attempt to re-claim on the next mount.
          localStorage.removeItem('pendingReferralInviteToken');
          if (urlClaimToken) {
            searchParams.delete('claimToken');
            setSearchParams(searchParams);
          }
        }
      };

      claimReferral();
    }
  }, [searchParams, user?.id, showSuccess, showError, setSearchParams]);

  const { data: referralsResponse, isPending: referralsLoading } = useQuery<ApiResponse<Referral[]>>({
    queryKey: ["/api/workflows/referral/list"],
    queryFn: async () => {
      const response = await fetch('/api/workflows/referral/list', {
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to fetch referrals');
      }

      return response.json();
    },
  });

  const currentUserId = user?.id;

  // Extract referrals array with type safety - API returns { data: [...], success: true }
  const referralsArray = extractArrayData<Referral>(referralsResponse);

  // Referral opened via ?id= (e.g. from a notification). Resolved against the
  // unfiltered list so it still opens when the referral is hidden for this user
  // or excluded by the active filters.
  const deepLinkedReferralId = searchParams.get('id');
  const deepLinkedReferral = deepLinkedReferralId
    ? referralsArray.find((referral) => String(referral.id) === deepLinkedReferralId)
    : undefined;

  const closeDeepLinkedReferral = () => {
    searchParams.delete('id');
    setSearchParams(searchParams, { replace: true });
  };

  // Extract unique partners for the partner filter dropdown
  const uniquePartners = useMemo(() => {
    const partners = new Map<string, { id: string; name: string }>();
    referralsArray.forEach((referral) => {
      // If user is sender, partner is receiver
      if (referral.senderId === currentUserId && referral.receiverId) {
        const partnerName = referral.receiverName || "Unknown Partner";
        partners.set(referral.receiverId, { id: referral.receiverId, name: partnerName });
      }
      // If user is receiver, partner is sender
      if (referral.receiverId === currentUserId && referral.senderId) {
        const partnerName = referral.senderName || "Unknown Partner";
        partners.set(referral.senderId, { id: referral.senderId, name: partnerName });
      }
    });
    return Array.from(partners.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [referralsArray, currentUserId]);

  // Headline counts for each view, computed from the full list rather than the
  // current filter selection, so the four numbers always add up on screen.
  // `active` is the definition the dashboard's Active Referrals badge uses; a
  // referral the user has hidden is not active, which is why `hiddenActive`
  // exists as its own view instead of being buried inside "Show all".
  const viewCounts = useMemo(() => {
    const involved = referralsArray.filter(
      (referral) => referral.senderId === currentUserId || referral.receiverId === currentUserId
    );
    const isHidden = (referral: Referral) =>
      ((referral.hiddenFor as string[]) || []).includes(currentUserId || '');
    const isActive = (referral: Referral) => {
      const status = ((referral as any).groupStatus || referral.status)?.toLowerCase();
      return !INACTIVE_STATUSES.includes(status);
    };

    return {
      active: involved.filter((r) => !isHidden(r) && isActive(r)).length,
      hiddenActive: involved.filter((r) => isHidden(r) && isActive(r)).length,
      visible: involved.filter((r) => !isHidden(r)).length,
      total: involved.length,
    };
  }, [referralsArray, currentUserId, INACTIVE_STATUSES]);

  // Jump straight to one of the four views above.
  const applyView = (view: 'active' | 'hiddenActive' | 'visible' | 'total') => {
    setStatusFilter('all');
    setShowInactiveReferrals(view === 'visible' || view === 'total');
    setHiddenFilter(view === 'hiddenActive' ? 'only' : view === 'total' ? 'include' : 'exclude');
  };

  // Check if any filters are active (not counting the inactive toggle as a "filter")
  const hasActiveFilters = statusFilter !== "all" || partnerFilter !== "all" || urgencyFilter !== "all" || hiddenFilter !== "exclude" || dateRange.from || dateRange.to || searchQuery;

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPartnerFilter("all");
    setUrgencyFilter("all");
    setHiddenFilter("exclude");
    setDateRange({ from: undefined, to: undefined });
    // Also show all referrals when clearing filters
    setShowInactiveReferrals(true);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab, searchQuery, statusFilter, partnerFilter, urgencyFilter, hiddenFilter, dateRange.from, dateRange.to, showInactiveReferrals]);

  // Update URL when toggle changes (and vice versa sync on mount)
  useEffect(() => {
    // If showing inactive (all), set showAll=true; otherwise remove it
    if (showInactiveReferrals) {
      searchParams.set('showAll', 'true');
    } else {
      searchParams.delete('showAll');
    }
    // Also remove the old activeOnly param if present
    searchParams.delete('activeOnly');
    setSearchParams(searchParams, { replace: true });
  }, [showInactiveReferrals]);

  // Filter referrals based on all criteria
  const filteredReferralsRaw = useMemo(() => {
    return referralsArray.filter((referral) => {
      // Check if referral is hidden by current user
      const hiddenFor = referral.hiddenFor as string[] || [];
      const isHidden = hiddenFor.includes(currentUserId || '');

      // Apply the hidden filter. This is its own axis, so it composes with the
      // status filter instead of competing with it - "Pending" + "Include hidden"
      // means every pending referral whether or not this user hid it.
      if (hiddenFilter === "exclude" && isHidden) return false;
      if (hiddenFilter === "only" && !isHidden) return false;

      // Must be involved in referral
      const isInvolved = referral.senderId === currentUserId || referral.receiverId === currentUserId;
      if (!isInvolved) return false;

      // Apply tab filter (Sent / Received / All)
      if (activeTab === "sent" && referral.senderId !== currentUserId) return false;
      if (activeTab === "received" && referral.receiverId !== currentUserId) return false;

      // Apply search filter (partner name, company, client name)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const searchableFields = [
          referral.senderName,
          referral.receiverName,
          referral.senderTrade,
          referral.receiverTrade,
          referral.clientName,
          referral.jobDescription,
        ].filter(Boolean);

        const matchesSearch = searchableFields.some(field =>
          field?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // Apply status filter
      if (statusFilter !== "all") {
        // For group referrals (sent by current user), use group status; otherwise use individual status
        const effectiveStatus = (referral as any).groupStatus || referral.status;
        if (effectiveStatus?.toLowerCase() !== statusFilter) {
          return false;
        }
      }

      // Filter out inactive referrals if toggle is off. Only meaningful when no
      // explicit status is picked - choosing a status is a stronger statement of
      // intent than the Active toggle.
      if (!showInactiveReferrals && statusFilter === "all") {
        const effectiveStatus = ((referral as any).groupStatus || referral.status)?.toLowerCase();
        if (INACTIVE_STATUSES.includes(effectiveStatus)) {
          return false;
        }
      }

      // Apply partner filter
      if (partnerFilter !== "all") {
        const isSent = referral.senderId === currentUserId;
        const partnerId = isSent ? referral.receiverId : referral.senderId;
        if (partnerId !== partnerFilter) return false;
      }

      // Apply urgency filter
      if (urgencyFilter !== "all") {
        const urgencyLevel = (referral as any).urgencyLevel?.toLowerCase() || 'normal';
        if (urgencyLevel !== urgencyFilter) return false;
      }

      // Apply date range filter
      if (dateRange.from || dateRange.to) {
        const referralDate = new Date(referral.createdAt);
        if (dateRange.from && referralDate < dateRange.from) return false;
        if (dateRange.to) {
          const endOfDay = new Date(dateRange.to);
          endOfDay.setHours(23, 59, 59, 999);
          if (referralDate > endOfDay) return false;
        }
      }

      return true;
    });
  }, [referralsArray, currentUserId, activeTab, searchQuery, statusFilter, partnerFilter, urgencyFilter, dateRange, showInactiveReferrals, hiddenFilter, INACTIVE_STATUSES]);

  // Group referrals by referral_group_id for display
  // For sent group referrals, show only ONE card representing the entire group
  const filteredReferrals = useMemo(() => {
    const grouped: Map<string | null, Referral[]> = new Map();
    const ungrouped: Referral[] = [];

    filteredReferralsRaw.forEach((ref: any) => {
      // Get referral_group_id (handle both snake_case and camelCase)
      const groupId = ref.referral_group_id || ref.referralGroupId;
      // Get sender_id (handle both snake_case and camelCase)
      const senderId = ref.senderId || ref.sender_id;

      if (groupId && senderId === currentUserId) {
        // This is a group referral sent by the current user
        const key = groupId;
        if (!grouped.has(key)) {
          grouped.set(key, []);
        }
        grouped.get(key)!.push(ref);
      } else {
        // Either not a group referral, or received group referral (show individually)
        ungrouped.push(ref);
      }
    });

    const result: Referral[] = [];

    // Add ungrouped referrals
    result.push(...ungrouped);

    // Add one representative referral for each group
    grouped.forEach((groupRefs, groupId) => {
      if (groupRefs.length > 0) {
        // Use the first referral as the representative, but add group metadata
        const representative = {
          ...groupRefs[0],
          _isGroupRepresentative: true,
          _groupSize: groupRefs.length,
          _groupReferrals: groupRefs,
        } as any;
        result.push(representative);
      }
    });

    // Sort by creation date
    return result.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [filteredReferralsRaw, currentUserId]);

  // Paginate filtered referrals
  const totalReferrals = filteredReferrals.length;
  const totalPages = Math.ceil(totalReferrals / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReferrals = filteredReferrals.slice(startIndex, endIndex);

  return (
    <AppLayout activeTab="referrals" title="Referrals">
      {/* Same column cap as Dashboard/Settings/Account. Without it this page was the one
          authenticated screen that ran edge-to-edge on an iPad (1366pt landscape), putting the
          partner name and the status badges at opposite ends of a 1330px-wide card. `max-w-md`
          is 448px, wider than any iPhone, so the phone layout is untouched. */}
      <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto px-4 pt-[9px] md:pt-[4px] pb-6">
          {/* Tabs */}
          <div className="flex space-x-1 bg-white shadow-md border border-gray-200 rounded-2xl p-1.5 mb-6">
            <button
              onClick={() => setActiveTab("all")}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                activeTab === "all"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              All Referrals
            </button>
            <button
              onClick={() => setActiveTab("sent")}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                activeTab === "sent"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Sent
            </button>
            <button
              onClick={() => setActiveTab("received")}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                activeTab === "received"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Received
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by partner name, company, or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 py-3 w-full bg-white border-gray-300"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Toggle & Active Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-600' : ''}`}
            >
              <Filter className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>

            {/* Show/Hide Inactive Referrals Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInactiveReferrals(!showInactiveReferrals)}
              className={`flex items-center gap-2 ${showInactiveReferrals ? 'bg-gray-50 border-gray-300 text-gray-600' : 'bg-blue-50 border-blue-200 text-blue-600'}`}
            >
              {showInactiveReferrals ? (
                <>
                  <Eye className="w-4 h-4" />
                  Showing All
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4" />
                  Active Only
                </>
              )}
            </Button>

            {/* Active Filter Badges */}
            {hasActiveFilters && (
              <>
                {hiddenFilter !== "exclude" && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1">
                    {HIDDEN_FILTERS.find(h => h.value === hiddenFilter)?.label}
                    <button onClick={() => setHiddenFilter("exclude")} className="ml-1 hover:bg-amber-200 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {statusFilter !== "all" && (
                  <Badge className="bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-1">
                    Status: {REFERRAL_STATUSES.find(s => s.value === statusFilter)?.label}
                    <button onClick={() => setStatusFilter("all")} className="ml-1 hover:bg-blue-200 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {partnerFilter !== "all" && (
                  <Badge className="bg-purple-100 text-purple-700 border border-purple-200 flex items-center gap-1">
                    Partner: {uniquePartners.find(p => p.id === partnerFilter)?.name}
                    <button onClick={() => setPartnerFilter("all")} className="ml-1 hover:bg-purple-200 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {(dateRange.from || dateRange.to) && (
                  <Badge className="bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
                    Date: {dateRange.from ? format(dateRange.from, "MMM d") : "Start"} - {dateRange.to ? format(dateRange.to, "MMM d") : "End"}
                    <button onClick={() => setDateRange({ from: undefined, to: undefined })} className="ml-1 hover:bg-green-200 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {urgencyFilter !== "all" && (
                  <Badge className="bg-orange-100 text-orange-700 border border-orange-200 flex items-center gap-1">
                    Urgency: {URGENCY_LEVELS.find(u => u.value === urgencyFilter)?.label}
                    <button onClick={() => setUrgencyFilter("all")} className="ml-1 hover:bg-orange-200 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </Button>
              </>
            )}
          </div>

          {/* View breakdown - makes the dashboard's Active Referrals count reconcilable.
              Every referral lands in exactly one of "Active" or "Hidden active" or an
              inactive status, so the numbers can be read against each other. */}
          <div className="view-filter-row mb-4">
            {([
              { key: 'active', label: 'Active', count: viewCounts.active, active: !showInactiveReferrals && statusFilter === 'all' && hiddenFilter === 'exclude' },
              { key: 'hiddenActive', label: 'Hidden active', count: viewCounts.hiddenActive, active: !showInactiveReferrals && statusFilter === 'all' && hiddenFilter === 'only' },
              { key: 'visible', label: 'All', count: viewCounts.visible, active: showInactiveReferrals && statusFilter === 'all' && hiddenFilter === 'exclude' },
              { key: 'total', label: 'All + hidden', count: viewCounts.total, active: showInactiveReferrals && statusFilter === 'all' && hiddenFilter === 'include' },
            ] as const).map((view) => (
              <button
                key={view.key}
                onClick={() => applyView(view.key)}
                className={`view-filter-chip rounded-full border font-medium transition-colors ${
                  // Only the long labels are allowed to ellipsize when the row runs out
                  // of room, so "Active" and "All" always read in full.
                  view.label.length > 8 ? 'view-filter-chip--shrink' : ''
                } ${
                  view.active
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="view-filter-chip__label">{view.label}</span>
                <span className="view-filter-chip__count">{view.count}</span>
              </button>
            ))}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <Card className="mb-4 border border-gray-200">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        {REFERRAL_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Hidden Filter - its own axis, so it combines with Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hidden
                    </label>
                    <Select value={hiddenFilter} onValueChange={(value) => setHiddenFilter(value as HiddenFilter)}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Hide hidden" />
                      </SelectTrigger>
                      <SelectContent>
                        {HIDDEN_FILTERS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Partner Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Partner
                    </label>
                    <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Partners" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Partners</SelectItem>
                        {uniquePartners.map((partner) => (
                          <SelectItem key={partner.id} value={partner.id}>
                            {partner.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Range
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateRange.from ? (
                            dateRange.to ? (
                              <>
                                {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d")}
                              </>
                            ) : (
                              format(dateRange.from, "MMM d, yyyy")
                            )
                          ) : (
                            <span className="text-gray-500">Pick a date range</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          initialFocus
                          mode="range"
                          defaultMonth={dateRange.from}
                          selected={dateRange}
                          onSelect={(range) => setDateRange(range || { from: undefined, to: undefined })}
                          numberOfMonths={2}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  {/* Urgency Level Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Urgency Level
                    </label>
                    <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Urgency Levels" />
                      </SelectTrigger>
                      <SelectContent>
                        {URGENCY_LEVELS.map((urgency) => (
                          <SelectItem key={urgency.value} value={urgency.value}>
                            {urgency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Availability Requests Panel */}
          <AvailabilityRequestsPanel outerTab={activeTab as 'all' | 'received' | 'sent'} />

          {/* Results Count */}
          {!referralsLoading && (
            <div className="mb-4 text-sm text-gray-500">
              Showing {filteredReferrals.length} {filteredReferrals.length === 1 ? 'referral' : 'referrals'}
              {hasActiveFilters && ' (filtered)'}
            </div>
          )}

          {/* Referrals List */}
          {referralsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="border border-gray-200">
                  <CardContent className="p-4">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredReferrals.length === 0 ? (
            <Card className="border border-gray-200">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-gray-400" />
                </div>
                {hasActiveFilters ? (
                  <>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No matching referrals</h3>
                    <p className="text-gray-500 mb-4">
                      Try adjusting your search or filters to find what you're looking for
                    </p>
                    <Button
                      onClick={clearFilters}
                      variant="outline"
                    >
                      Clear Filters
                    </Button>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No referrals yet</h3>
                    <p className="text-gray-500 mb-4">
                      Start building your network by sending your first referral
                    </p>
                    <Link to="/send-referral">
                      <Button className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-md">
                        Send First Referral
                      </Button>
                    </Link>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="space-y-3">
                {paginatedReferrals.map((referral) => (
                  <CompactReferralCard key={referral.id} referral={referral} />
                ))}
              </div>

              {/* Pagination Controls */}
              {totalReferrals > itemsPerPage && (
                <div className="flex items-center justify-between mt-6 px-2">
                  <div className="text-sm text-gray-500">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalReferrals)} of {totalReferrals}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => p + 1)}
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
      </div>

      {deepLinkedReferral && (
        <ReferralDetailModal
          referral={{ ...deepLinkedReferral, id: Number(deepLinkedReferral.id) }}
          isOpen={true}
          onClose={closeDeepLinkedReferral}
        />
      )}
    </AppLayout>
  );
}
